'use client';
import { useState, useEffect } from 'react';
import Link from 'next/link';
import { usePathname, useRouter } from 'next/navigation';
import { groupCategories } from '@/_config';
import { BottomSheet } from './BottomSheet';

const HIDDEN_ON = ['/checkout', '/payment'];

const itemStyle = (active) => ({
    flex: '1 1 0',
    minWidth: 0,
    minHeight: 56,
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 3,
    padding: '6px 0',
    background: 'none',
    border: 'none',
    color: active ? 'var(--primary-color)' : '#64748b',
    fontSize: '0.66rem',
    fontWeight: active ? 700 : 500,
    textDecoration: 'none',
    cursor: 'pointer',
    position: 'relative',
    transition: 'color 0.2s ease',
    WebkitTapHighlightColor: 'transparent',
});

const iconStyle = { fontSize: '1.2rem' };

export const MobileBottomNav = ({ categories: rawCategories = [] }) => {
    const router = useRouter();
    const pathname = usePathname();
    const [loggedIn, setLoggedIn] = useState(false);
    const [sheetOpen, setSheetOpen] = useState(false);
    const categories = groupCategories(rawCategories);

    useEffect(() => {
        const syncAuth = () => setLoggedIn(/(?:^|; )authUser=/.test(document.cookie));
        syncAuth();
        window.addEventListener('ennix:auth-changed', syncAuth);
        return () => {
            window.removeEventListener('ennix:auth-changed', syncAuth);
        };
    }, []);

    if (HIDDEN_ON.some((p) => pathname?.startsWith(p))) return null;

    const openCategories = () => setSheetOpen(true);

    const goToCategory = (slug) => {
        setSheetOpen(false);
        router.push(`/${slug}`);
    };

    const openSearch = () => {
        const input = document.querySelector('.inline-search-input');
        if (input) {
            window.scrollTo({ top: 0, behavior: 'smooth' });
            setTimeout(() => input.focus(), 250);
        } else {
            router.push('/search');
        }
    };

    return (
        <>
            <nav className="ennix-bottom-nav" aria-label="Main navigation">
                <Link href="/" style={itemStyle(pathname === '/')} aria-current={pathname === '/' ? 'page' : undefined}>
                    <i className="pi pi-home" style={iconStyle} aria-hidden="true" />
                    <span>Home</span>
                </Link>
                <button type="button" onClick={openCategories} style={itemStyle(sheetOpen)}>
                    <i className="pi pi-th-large" style={iconStyle} aria-hidden="true" />
                    <span>Categories</span>
                </button>
                <button type="button" onClick={openSearch} style={itemStyle(pathname === '/search')}>
                    <i className="pi pi-search" style={iconStyle} aria-hidden="true" />
                    <span>Search</span>
                </button>
                <Link href={loggedIn ? '/account' : '/login'} style={itemStyle(pathname?.startsWith('/account'))}>
                    <i className="pi pi-user" style={iconStyle} aria-hidden="true" />
                    <span>Account</span>
                </Link>
            </nav>

            <BottomSheet visible={sheetOpen} onHide={() => setSheetOpen(false)} title="Shop by category">
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                    gap: 10,
                    padding: '12px 16px 20px',
                }}>
                    {categories.length === 0 && (
                        <p style={{ gridColumn: 'span 2', color: '#64748b', fontSize: '0.85rem', margin: 0 }}>No categories available.</p>
                    )}
                    {categories.map((cat) => (
                        <button
                            key={cat._id ?? cat.slug}
                            type="button"
                            onClick={() => goToCategory(cat.slug)}
                            style={{
                                minHeight: 48,
                                padding: '10px 12px',
                                borderRadius: 12,
                                border: '1px solid #e2e8f0',
                                background: '#f8fafc',
                                color: '#0f172a',
                                fontSize: '0.85rem',
                                fontWeight: 600,
                                textAlign: 'left',
                                cursor: 'pointer',
                            }}
                        >
                            {cat.title}
                        </button>
                    ))}
                </div>
            </BottomSheet>
        </>
    );
};
